import React, {useEffect, useState, useRef} from 'react';
import {injectIntl} from 'react-intl';

const PhoneField = ({intl, settings, onChange, ...rest}) => {
  const phone = useRef(null);
  const {
    label,
    name,
    description,
    state,
    setState,
    errors,
    placeholder,
    required = true,
    hidden = false,
    disabled = false,
    numberFormat = '(***) ***-****',
    inputMask = '(___) ___-____',
  } = settings || {};

  if (hidden) {
    return '';
  }

  const [localState, setLocalState] = useState('(___) ___-____');
  const [focus, setFocus] = useState(false);

  const hasErrors = (errors && ((errors.hasOwnProperty(name) && errors[name].length)));

  useEffect(() => {
    if (state && state[name]) {
      setLocalState(doFormat(state[name], numberFormat, inputMask));
    }
  }, []);

  useEffect(() => {
    setState((prev) => {
      if (!prev) {
        prev = {};
      }

      return {
        ...prev,
        [name]: removeFormat(localState),
      };
    });

    const val = doFormat(removeFormat(localState), numberFormat);
    const elem = phone.current;
    if (elem.createTextRange) {
      const range = elem.createTextRange();
      range.move('character', val.length);
      range.select();
    } else if (elem.selectionStart) {
      if (focus) {
        elem.focus();
      }
      elem.setSelectionRange(val.length, val.length);
    }

    if (!focus) {
      setFocus(true);
    }
  }, [localState]);

  const doFormat = (x, pattern, mask) => {
    const strippedValue = x.replace(/[^0-9]/g, '');
    const chars = strippedValue.split('');
    let count = 0;

    let formatted = '';
    for (let i = 0; i < pattern.length; i++) {
      const c = pattern[i];
      if (chars[count]) {
        if (/\*/.test(c)) {
          formatted += chars[count];
          count++;
        } else {
          formatted += c;
        }
      } else if (mask) {
        if (mask.split('')[i]) {
          formatted += mask.split('')[i];
        }
      }
    }

    return formatted;
  };

  const removeFormat = (value) => {
    return value.replace(/\D/g, '');
  };

  const format = (event) => {
    const elem = event.target;
    setLocalState(doFormat(elem.value, numberFormat, inputMask));
  };

  const handleChange = (event) => {
    event.persist();
    format(event);
  };

  const renderLabel = () => {
    if (!label) {
      return <label className="form-group__label" />;
    } else {
      return (
        <label className="form-group__label" htmlFor={name}>
          {intl.formatMessage({id: label})}
          {!required &&
            <span className="form-group__optional"> {intl.formatMessage({id: 'FormElements.Label.Optional'})}</span>
          }
        </label>
      );
    }
  };

  const renderDescription = () => {
    if (!description) {
      return '';
    }
    return (
      <div className="form-group__message">
        {intl.formatMessage({id: description})}
      </div>
    );
  };

  const renderErrors = () => {
    const renderable = [];
    if (hasErrors && errors.hasOwnProperty(name) && errors[name].length) {
      renderable.push(
          <div key={`errorField-${name}`} className="form-group__feedback">{intl.formatMessage({id: errors[name]})}</div>,
      );
    }
    return renderable;
  };

  const renderPhoneField = () => (
    <div className={`form-group ${hasErrors ? 'form-group--error' : ''}`}>
      {renderLabel()}
      <input
        ref={phone}
        type="text"
        name={name}
        value={localState}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        data-format="(***) ***-****"
        data-mask="(___) ___-____"
        onClick={format}
        {...rest}
      />
      {renderDescription()}
      {renderErrors()}
    </div>
  );

  return (
    <>
      {renderPhoneField()}
    </>
  );
};

export default injectIntl(PhoneField);
